import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Volume2, Check, X, RotateCcw, Target, BookOpen, Users } from 'lucide-react';
import { taskBasedExercises } from '@/data/whWordsData';

interface TaskBasedExercisesProps {
  currentLanguage: 'english' | 'french';
  speakText: (text: string, lang?: 'en-US' | 'fr-FR') => void;
}

const TaskBasedExercises: React.FC<TaskBasedExercisesProps> = ({ currentLanguage, speakText }) => {
  const [fillInAnswers, setFillInAnswers] = useState<string[]>(new Array(taskBasedExercises.fillInTheBlanks.length).fill(''));
  const [multipleChoiceAnswers, setMultipleChoiceAnswers] = useState<string[]>(new Array(taskBasedExercises.multipleChoice.length).fill(''));
  const [matchingAnswers, setMatchingAnswers] = useState<string[]>(new Array(taskBasedExercises.matching.length).fill(''));
  const [showResults, setShowResults] = useState<{[key: string]: boolean}>({});

  const resetExercise = (type: string) => {
    if (type === 'fillIn') {
      setFillInAnswers(new Array(taskBasedExercises.fillInTheBlanks.length).fill(''));
    } else if (type === 'multipleChoice') {
      setMultipleChoiceAnswers(new Array(taskBasedExercises.multipleChoice.length).fill(''));
    } else if (type === 'matching') {
      setMatchingAnswers(new Array(taskBasedExercises.matching.length).fill(''));
    }
    setShowResults(prev => ({ ...prev, [type]: false }));
  };

  const checkAnswers = (type: string) => {
    setShowResults(prev => ({ ...prev, [type]: true }));
  };

  const updateFillInAnswer = (index: number, value: string) => {
    const newAnswers = [...fillInAnswers];
    newAnswers[index] = value;
    setFillInAnswers(newAnswers);
  };

  const updateMultipleChoiceAnswer = (index: number, value: string) => {
    const newAnswers = [...multipleChoiceAnswers];
    newAnswers[index] = value;
    setMultipleChoiceAnswers(newAnswers);
  };

  const updateMatchingAnswer = (index: number, value: string) => {
    const newAnswers = [...matchingAnswers];
    newAnswers[index] = value;
    setMatchingAnswers(newAnswers);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-primary mb-4">
          {currentLanguage === 'english' ? 'Task-Based Exercises' : 'Exercices Basés sur les Tâches'}
        </h2>
        <p className="text-muted-foreground mb-6">
          {currentLanguage === 'english' 
            ? 'Practice using Wh-questions in daily routine and personal information contexts'
            : 'Pratiquez l\'utilisation des questions en anglais dans des contextes de routine quotidienne et d\'informations personnelles'}
        </p>
        
        <div className="flex justify-center gap-4 mb-6">
          <Badge variant="secondary" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            {currentLanguage === 'english' ? 'Daily Routines' : 'Routines Quotidiennes'}
          </Badge>
          <Badge variant="secondary" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            {currentLanguage === 'english' ? 'Personal Information' : 'Informations Personnelles'}
          </Badge>
          <Badge variant="secondary" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            {currentLanguage === 'english' ? 'Family Context' : 'Contexte Familial'}
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="fillIn" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="fillIn">
            {currentLanguage === 'english' ? 'Fill in the Blanks' : 'Compléter les Phrases'}
          </TabsTrigger>
          <TabsTrigger value="multipleChoice">
            {currentLanguage === 'english' ? 'Multiple Choice' : 'Choix Multiple'}
          </TabsTrigger>
          <TabsTrigger value="matching">
            {currentLanguage === 'english' ? 'Matching' : 'Correspondance'}
          </TabsTrigger>
        </TabsList>

        {/* Fill in the Blanks */}
        <TabsContent value="fillIn">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{currentLanguage === 'english' ? 'Fill in the Blanks' : 'Compléter les Phrases'}</span>
                <div className="flex gap-2">
                  <Button onClick={() => resetExercise('fillIn')} variant="outline" size="sm">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {currentLanguage === 'english' ? 'Reset' : 'Réinitialiser'}
                  </Button>
                  <Button onClick={() => checkAnswers('fillIn')} size="sm">
                    <Check className="w-4 h-4 mr-2" />
                    {currentLanguage === 'english' ? 'Check Answers' : 'Vérifier'}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {taskBasedExercises.fillInTheBlanks.map((exercise, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-secondary/30">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="default">English</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => speakText(exercise.question.replace('_____', exercise.answer))}
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-lg mb-3">{exercise.question}</p>
                        <Input
                          placeholder={currentLanguage === 'english' ? 'Your answer...' : 'Votre réponse...'}
                          value={fillInAnswers[index]}
                          onChange={(e) => updateFillInAnswer(index, e.target.value)}
                          className="mb-2"
                        />
                        {showResults.fillIn && (
                          <div className="flex items-center gap-2">
                            {fillInAnswers[index].toLowerCase() === exercise.answer.toLowerCase() ? (
                              <Check className="w-5 h-5 text-green-600" />
                            ) : (
                              <X className="w-5 h-5 text-red-600" />
                            )}
                            <span className={`text-sm ${
                              fillInAnswers[index].toLowerCase() === exercise.answer.toLowerCase() 
                                ? 'text-green-600' 
                                : 'text-red-600'
                            }`}>
                              {fillInAnswers[index].toLowerCase() === exercise.answer.toLowerCase() 
                                ? (currentLanguage === 'english' ? 'Correct!' : 'Correct!')
                                : `${currentLanguage === 'english' ? 'Correct answer:' : 'Réponse correcte:'} ${exercise.answer}`
                              }
                            </span>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="default" className="bg-accent">Français</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => speakText(exercise.french, 'fr-FR')}
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-lg text-accent">{exercise.french}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Multiple Choice */}
        <TabsContent value="multipleChoice">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{currentLanguage === 'english' ? 'Multiple Choice' : 'Choix Multiple'}</span>
                <div className="flex gap-2">
                  <Button onClick={() => resetExercise('multipleChoice')} variant="outline" size="sm">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {currentLanguage === 'english' ? 'Reset' : 'Réinitialiser'}
                  </Button>
                  <Button onClick={() => checkAnswers('multipleChoice')} size="sm">
                    <Check className="w-4 h-4 mr-2" />
                    {currentLanguage === 'english' ? 'Check Answers' : 'Vérifier'}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {taskBasedExercises.multipleChoice.map((exercise, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-secondary/30">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="default">English</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => speakText(exercise.question.replace('_____', exercise.answer))}
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-lg">{exercise.question}</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="default" className="bg-accent">Français</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => speakText(exercise.french, 'fr-FR')}
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-lg text-accent">{exercise.french}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {exercise.options.map((option, optionIndex) => (
                        <Button
                          key={optionIndex}
                          variant={multipleChoiceAnswers[index] === option ? "default" : "outline"}
                          onClick={() => updateMultipleChoiceAnswer(index, option)}
                          className="w-full"
                        >
                          {option}
                        </Button>
                      ))}
                    </div>
                    
                    {showResults.multipleChoice && (
                      <div className="flex items-center gap-2 mt-3">
                        {multipleChoiceAnswers[index] === exercise.answer ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : (
                          <X className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`text-sm ${
                          multipleChoiceAnswers[index] === exercise.answer 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          {multipleChoiceAnswers[index] === exercise.answer 
                            ? (currentLanguage === 'english' ? 'Correct!' : 'Correct!')
                            : `${currentLanguage === 'english' ? 'Correct answer:' : 'Réponse correcte:'} ${exercise.answer}`
                          }
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Matching */}
        <TabsContent value="matching">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{currentLanguage === 'english' ? 'Matching Exercise' : 'Exercice de Correspondance'}</span>
                <div className="flex gap-2">
                  <Button onClick={() => resetExercise('matching')} variant="outline" size="sm">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {currentLanguage === 'english' ? 'Reset' : 'Réinitialiser'}
                  </Button>
                  <Button onClick={() => checkAnswers('matching')} size="sm">
                    <Check className="w-4 h-4 mr-2" />
                    {currentLanguage === 'english' ? 'Check Answers' : 'Vérifier'}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-6">
                {currentLanguage === 'english' 
                  ? 'Match each question with the correct Wh-word that should fill the blank.'
                  : 'Associez chaque question avec le bon mot-question qui devrait remplir le blanc.'}
              </p>
              
              <div className="space-y-4">
                {taskBasedExercises.matching.map((exercise, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-secondary/30">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="default">English</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => speakText(exercise.english.replace('_____', exercise.whWord))}
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-lg">{exercise.english.replace(exercise.whWord, '_____')}</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="default" className="bg-accent">Français</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => speakText(exercise.french, 'fr-FR')}
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-lg text-accent">{exercise.french}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {['Who', 'What', 'Where', 'When', 'Why', 'Which', 'Whose'].map((word) => (
                        <Button
                          key={word}
                          variant={matchingAnswers[index] === word ? "default" : "outline"}
                          onClick={() => updateMatchingAnswer(index, word)}
                          size="sm"
                        >
                          {word}
                        </Button>
                      ))}
                    </div>
                    
                    {showResults.matching && (
                      <div className="flex items-center gap-2 mt-3">
                        {matchingAnswers[index] === exercise.whWord ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : (
                          <X className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`text-sm ${
                          matchingAnswers[index] === exercise.whWord 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          {matchingAnswers[index] === exercise.whWord 
                            ? (currentLanguage === 'english' ? 'Correct!' : 'Correct!')
                            : `${currentLanguage === 'english' ? 'Correct answer:' : 'Réponse correcte:'} ${exercise.whWord}`
                          }
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TaskBasedExercises;